"use client"

import { BadgeCheck, Bell, ChevronsUpDown, CreditCard, LogOut, Sparkles } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar"
import { useAuth } from "@/components/auth-provider"
export function NavUser({
  user,
  /** Client dashboard: only profile header + Log out (no Upgrade / Account / Billing / Notifications). */
  minimalMenu = false,
}: {
  user: {
    name: string
    email: string
    avatar?: string
  }
  minimalMenu?: boolean
}) {
  const { isMobile } = useSidebar()
  const { logout, user: authUser } = useAuth()

  const handleLogout = () => {
    logout()
  }

  const displayUser = authUser || user

  // Extract initials from email (first two characters)
  const getInitials = (email: string): string => {
    return email.slice(0, 2).toUpperCase()
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={displayUser.name} />
                <AvatarFallback className="rounded-lg">{getInitials(displayUser.email)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{displayUser.name}</span>
                <span className="truncate text-xs">{displayUser.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={displayUser.name} />
                  <AvatarFallback className="rounded-lg">{getInitials(displayUser.email)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{displayUser.name}</span>
                  <span className="truncate text-xs">{displayUser.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            {!minimalMenu && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {/* RELEASE_HIDDEN: Disabled for production */}
                  <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                    <Sparkles />
                    Upgrade to Pro
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {/* RELEASE_HIDDEN: Disabled for production */}
                  <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                    <BadgeCheck />
                    Account
                  </DropdownMenuItem>
                  {/* RELEASE_HIDDEN: Disabled for production */}
                  <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                    <CreditCard />
                    Billing
                  </DropdownMenuItem>
                  {/* RELEASE_HIDDEN: Disabled for production */}
                  <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed" onClick={(e) => e.preventDefault()}>
                    <Bell />
                    Notifications
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            {minimalMenu && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={handleLogout} className="!text-red-600 hover:!bg-[#E04041] hover:!text-white cursor-pointer focus:!bg-[#E04041] focus:!text-white data-[highlighted]:!bg-[#E04041] data-[highlighted]:!text-white">
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
